import AbortablePromise from "../../../../../utils/promise/AbortablePromise";
import {deleteComponent, saveComponent} from "../../../../../utils/components";
import {sendRequest} from "../../../../../utils/request";
import {set} from "../../../../../utils/dotutils";
import DataProviderComp from "./DataProviderComp";
import {RequestParams} from "../Types/RequestParams";
import {Items} from "../Types/Items";
import {ProviderProps} from "../Types/ProviderProps";
import {DataGridProps} from "../Types/DataGridProps";
import {ComponentConfigType} from "../../../../../utils/ComponentConfigType";

type ComponentListRequestParams = RequestParams & {
    component: string,
}

export type ComponentListDataProviderProps = DataGridProps & ProviderProps & {
    option?: string,
    component?: string,
    componentConfig: ComponentConfigType
}

export default class ComponentListDataProvider<Props extends ComponentListDataProviderProps, State> extends DataProviderComp<Props, State> {

    static defaultProps = {
        selectorFunction: ({item, items, selection, props, selectionType = "index"}) => {
            switch (selectionType) {
                case "selectable":
                    return items.some((selected) => {
                        if (selected === item) return true;
                        if (selected instanceof Object && item instanceof Object) {
                            return selected[props.componentConfig.idfield] + "" === "" + item[props.componentConfig.idfield];
                        } else if (selected !== undefined && item !== undefined) {
                            return selected + "" === "" + item;
                        } else if (selected instanceof Object && !(item instanceof Object)) {
                            return selected[props.componentConfig.idfield] + "" === "" + item;
                        } else if (!(selected instanceof Object) && item instanceof Object) {
                            return selected + "" === "" + item[props.componentConfig.idfield];
                        }
                    });
                case "index":
                    return selection.findIndex((selected) => {
                        if (selected === item) return true;
                        if (selected instanceof Object && item instanceof Object) {
                            return selected[props.componentConfig.idfield] + "" === "" + item[props.componentConfig.idfield];
                        } else if (selected !== undefined && item !== undefined) {
                            return selected + "" === "" + item;
                        } else if (selected instanceof Object && !(item instanceof Object)) {
                            return selected[props.componentConfig.idfield] + "" === "" + item;
                        } else if (!(selected instanceof Object) && item instanceof Object) {
                            return selected + "" === "" + item[props.componentConfig.idfield];
                        }
                    });
                case "id":
                    let selected = selection.find((selected) => {
                        if (selected === item) return true;
                        if (selected instanceof Object && item instanceof Object) {
                            return selected[props.componentConfig.idfield] + "" === "" + item[props.componentConfig.idfield];
                        } else if (selected !== undefined && item !== undefined) {
                            return selected + "" === "" + item;
                        } else if (selected instanceof Object && !(item instanceof Object)) {
                            return selected[props.componentConfig.idfield] + "" === "" + item;
                        } else if (!(selected instanceof Object) && item instanceof Object) {
                            return selected + "" === "" + item[props.componentConfig.idfield];
                        }
                    });
                    return selected && selected instanceof Object ? selected[props.componentConfig.idfield] : selected;
            }
        },
    }

    protected lastrequest: any;

    constructor(props) {
        super(props);
        this.addItems = this.addItems.bind(this);
        this.editItems = this.editItems.bind(this);
        this.removeItems = this.removeItems.bind(this);
        this.generateRequest = this.generateRequest.bind(this);
        this.loadData = this.loadData.bind(this);
        this.parseView = this.parseView.bind(this);
        this.rowKeyField = props.componentConfig.idfield ?? this.rowKeyField;
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.props.componentConfig.idfield !== prevProps.componentConfig.idfield) {
            this.rowKeyField = this.props.componentConfig.idfield ?? this.rowKeyField;
        }
    }

    async addItems(items: Items): Promise<any> {
        items = await super.addItems(items);
        let ret: void|AbortablePromise<any[]>|Items = items;
        let refresh = false;
        if (this.props.addItems) {
            ret = await this.props.addItems(items, this.props);
        }
        if (ret?.map instanceof Function) {
            await Promise.all(ret.map(async (item, idx) => {
                if (item) {
                    await saveComponent(this.props.componentConfig.componentname, item);
                }
            }).filter(Boolean));
            refresh = true;
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (refresh) {
            this.props.refresh({silent: true});
        }
    }

    async editItems(items: Items): Promise<any> {
        items = await super.editItems(items);
        let ret: void|AbortablePromise<any[]>|Items = items;
        let refresh = false;
        if (this.props.editItems) {
            ret = await this.props.editItems(items, this.props);
        }
        if (ret?.map instanceof Function) {
            await Promise.all(ret.map(async (item, idx) => {
                if (item) {
                    await saveComponent(this.props.componentConfig.componentname, item);
                }
            }).filter(Boolean));
            refresh = true;
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (refresh) {
            this.props.refresh({silent: true});
        }
    }

    async removeItems(items: Items): Promise<any> {
        items = await super.removeItems(items);
        let ret: void|AbortablePromise<any[]>|Items = items;
        let refresh = false;
        if (this.props.removeItems) {
            ret = await this.props.removeItems(items, this.props);
        }
        if (ret?.map instanceof Function) {
            await Promise.all(ret.map(async (item, idx) => {
                item = item instanceof Object ? item?.[this.props.componentConfig.idfield] : item;
                if (item) {
                    await deleteComponent(this.props.componentConfig.componentname, {
                        [this.props.componentConfig.idfield]: item
                    });
                }
            }).filter(Boolean));
            refresh = true;
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (refresh) {
            this.props.refresh({silent: true});
        }
    }

    generateRequest(props: Props, state: State): RequestParams {
        const request: ComponentListRequestParams = {
            ...super.generateRequest(props, state),
            component: props.componentConfig.componentname,
        };
        return request;
    }

    loadData(request: RequestParams): AbortablePromise<ResponseData> {
        return new AbortablePromise<ResponseData>((resolve, reject, signal) => {
            this.lastrequest && this.lastrequest.abort();
            signal.onAbort = (/*reason*/) => {
                this.lastrequest && this.lastrequest.abort();
            }
            let requestData = JSON.parse(JSON.stringify(request));
            this.lastrequest = sendRequest(requestData);
            this.lastrequest.then(async (response: ResponseData) => {
                if (!response.navigation) {
                    response.navigation = {
                        currpage: 1,
                        pagecount: 1,
                        rangeend: requestData.itemsperpage,
                        rangestart: 0,
                        link: null,
                    }
                }

                if (this.props.onLoadCompleted) {
                    const formattedData = await this.props.onLoadCompleted(response, requestData);
                    response = formattedData || response;
                }

                response.columns = response.columns || {};
                this.props.getFields().map((Column) => {
                    let field = Column.props.field;
                    let column = response.columns[field]?.label;
                    let label = column === field ? null : column;
                    label = label || Column?.props?.header || column || field;
                    response.columns[field] = {...(response.columns[field] || {}), label};
                });

                resolve(response);
            }).catch((e) => {
                reject(e);
            });
        });
    }

    parseView(props: Props): object {
        let view: Record<string, any> = {};
        if (props.view) {
            if (props.view instanceof Function) {
                view = props.view(props);
            }
            if (typeof props.view === "string") {
                if (props.componentConfig.views && props.componentConfig.views[props.view]) {
                    view = props.componentConfig.views[props.view];
                } else {
                    view = {};
                    //console.warn(`No ${props.view} view definition in ${props.componentConfig.componentname}!`);
                }
            } else if (Array.isArray(props.view)) {
                props.view.map((v) => set(view, v, ""));
            }
        }
        if (!view[props.componentConfig.idfield]) {
            set(view, props.componentConfig.idfield, "");
        }
        view = {...view};
        if (props.groupField) {
            set(view, props.groupField, "");
        }
        props.getFields().map((column) => set(view, column.props.field, ""));
        return view;
    }

}